import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  MenuItem,
  Divider,
  Typography,
  colors,
  InputAdornment,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  addLabourCostRate,
  updateLabourRate,
  LabourCostRateFormType,
  LabourCostRate,
  Role,
  getLabourRates,
} from 'api/labourApi';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import { grey } from '@mui/material/colors';
import { checkLabourCostRateDuplication } from '../Utils/Utils';
import DuplicateConfirmationDialog from './DuplicateConfirmationDialog';
import { useSnackbar } from 'notistack';
import { RATE_CARD_WORKING_AWAY_HEADER_TOOLTIP } from 'constants/workingAwayFromHomeCopy';

interface AddOrEditLabourCostRateDialogProps {
  open: boolean;
  onClose: () => void;
  editItem: LabourCostRate | null;
  onClearEdit?: () => void;
}

export default function AddOrEditLabourCostRateDialog({
  open,
  onClose,
  editItem,
  onClearEdit,
}: AddOrEditLabourCostRateDialogProps) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = !!editItem;
  const { data: labourData } = useQuery(['labour-rates'], getLabourRates);
  const [duplicateDialogConfirmOpen, setDuplicateDialogConfirmOpen] =
    useState(false);
  const [duplicatingRateId, setDuplicatingRateId] = useState<string | null>(
    null
  );

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm<LabourCostRateFormType>();

  const effectiveFrom = watch('effectiveFrom');

  const addMutation = useMutation(addLabourCostRate, {
    onSuccess: () => {
      queryClient.invalidateQueries(['labour-rates']);
      onClose();
      reset();
      onClearEdit?.();
      enqueueSnackbar('Labour cost rate added successfully', {
        variant: 'success',
      });
    },
    onError: () => {
      enqueueSnackbar('Failed to add labour cost rate', { variant: 'error' });
    },
  });

  const updateMutation = useMutation(updateLabourRate, {
    onSuccess: () => {
      queryClient.invalidateQueries(['labour-rates']);
      onClose();
      reset();
      onClearEdit?.();
      enqueueSnackbar('Labour cost rate updated successfully', {
        variant: 'success',
      });
    },
    onError: () => {
      enqueueSnackbar('Failed to update labour cost rate', {
        variant: 'error',
      });
    },
  });

  useEffect(() => {
    if (open && editItem) {
      reset({
        role: editItem.role,
        rate: Number(editItem.rate),
        workingAwayFromHomeRate: Number(editItem.workingAwayFromHomeRate),
        effectiveFrom: editItem.effectiveFrom.slice(0, 10),
      });
    } else if (open) {
      reset({
        effectiveFrom: new Date().toISOString(),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only run when dialog open or edit data changes
  }, [open, editItem]);

  const onSubmit = (data: LabourCostRateFormType) => {
    const updateData = {
      role: data.role,
      rate: data.rate,
      workingAwayFromHomeRate: data.workingAwayFromHomeRate,
      effectiveFrom: data.effectiveFrom,
    };
    const { checkRateDuplicate, foundRateId } = checkLabourCostRateDuplication(
      updateData,
      labourData?.costRates || []
    );
    if (checkRateDuplicate) {
      setDuplicateDialogConfirmOpen(true);
      setDuplicatingRateId(foundRateId);
      return;
    }
    if (editItem) {
      updateMutation.mutate({
        id: editItem.id,
        data: updateData,
        rateType: 'costRate',
      });
    } else {
      addMutation.mutate({ data });
    }
  };

  const isSubmitting = addMutation.isLoading || updateMutation.isLoading;

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') {
          onClose();
        }
      }}
      maxWidth="md"
      fullWidth>
      <DuplicateConfirmationDialog
        alertMessage={`You already have a cost rate for the selected role on the effective date. Do you want to override the existing rate or cancel to modify the effective date?`}
        isUpdatingRate={updateMutation.isLoading}
        onConfirm={() => {
          setDuplicateDialogConfirmOpen(false);
          if (duplicatingRateId) {
            updateMutation.mutate({
              id: duplicatingRateId,
              data: {
                role: watch('role'),
                rate: watch('rate'),
                workingAwayFromHomeRate: watch('workingAwayFromHomeRate'),
                effectiveFrom: watch('effectiveFrom'),
              },
              rateType: 'costRate',
            });
          }
        }}
        openConfirmationDialog={duplicateDialogConfirmOpen}
        setOpenConfirmationDialog={setDuplicateDialogConfirmOpen}
      />
      <DialogTitle sx={{ m: 1 }}>
        {isEdit ? 'Edit Labour Cost Rate' : 'Add Labour Cost Rate'}
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6} sx={{ mt: 4 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Controller
                  name="role"
                  control={control}
                  rules={{ required: 'Role is required' }}
                  render={(field) => (
                    <TextField
                      {...field}
                      select
                      label="Role"
                      fullWidth
                      required
                      error={!!errors.role}
                      helperText={errors.role?.message}>
                      <MenuItem value={Role.Administrator}>
                        Administrator
                      </MenuItem>
                      <MenuItem value={Role.ProjectManager}>
                        Project Manager
                      </MenuItem>
                      <MenuItem value={Role.Supervisor}>Supervisor</MenuItem>
                      <MenuItem value={Role.Technician}>Technician</MenuItem>
                    </TextField>
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="rate"
                  control={control}
                  rules={{
                    required: 'Rate is required',
                    min: { value: 0, message: 'Rate must be 0 or greater' },
                  }}
                  render={(field) => (
                    <TextField
                      {...field}
                      label="Rate"
                      type="number"
                      fullWidth
                      required
                      error={!!errors.rate}
                      helperText={errors.rate?.message}
                      inputProps={{ step: '0.01', min: '0' }}
                      onChange={(e) =>
                        field.onChange(parseFloat(e.target.value) || 0)
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">$</InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="workingAwayFromHomeRate"
                  control={control}
                  rules={{
                    required: 'Working away rate is required',
                    min: {
                      value: 0,
                      message: 'Rate must be 0 or greater',
                    },
                  }}
                  render={(field) => (
                    <TextField
                      {...field}
                      label="Working Away From Home Rate"
                      type="number"
                      fullWidth
                      required
                      error={!!errors.workingAwayFromHomeRate}
                      helperText={
                        errors.workingAwayFromHomeRate?.message ??
                        RATE_CARD_WORKING_AWAY_HEADER_TOOLTIP
                      }
                      inputProps={{ step: '0.01', min: '0' }}
                      onChange={(e) =>
                        field.onChange(parseFloat(e.target.value) || 0)
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">$</InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} md={6}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography
                  variant="body1"
                  sx={{ mb: '0.5rem', pl: 0.5, color: colors.grey[600] }}>
                  Effective From
                </Typography>
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                  <Controller
                    control={control}
                    name={'effectiveFrom'}
                    error={errors.effectiveFrom}
                    rules={{
                      required: {
                        value: true,
                        message: 'Effective From date is required',
                      },
                    }}
                    render={(props) => {
                      return (
                        <DatePicker
                          autoOk
                          fullWidth
                          // className={classes.mTop}
                          orientation="landscape"
                          error={Boolean(errors.effectiveFrom)}
                          variant="static"
                          helperText={errors?.effectiveFrom?.message}
                          openTo="date"
                          value={effectiveFrom}
                          defaultValue={
                            effectiveFrom ? effectiveFrom : new Date()
                          }
                          onChange={(e) => setValue('effectiveFrom', e)}
                        />
                      );
                    }}
                  />
                </MuiPickersUtilsProvider>
                {errors.effectiveFrom && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ paddingTop: 1, display: 'block' }}>
                    {errors.effectiveFrom.message}
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ mx: 2, my: 1 }}>
        <Button
          onClick={onClose}
          sx={{
            color: grey[700],
            mr: 1,
          }}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          disabled={isSubmitting}>
          {isSubmitting ? 'Saving...' : 'Save Rate'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
